import React, { useState } from 'react';
import { KibSheet } from '@chewy/kib-overlays-react';
import { KibButtonNew } from '@chewy/kib-controls-react';
import classNames from 'classnames';
import './avatar-selection-edit.scss';
import { useMutation } from 'react-query';
import { KibSpinner } from '@chewy/kib-interstitials-react';
import KibDivider from '@chewy/kib-divider-react';
import { PetAvatarUploadButton } from '../../../avatar/pet-avatar-upload-button';
import { uploadImage } from '../../../../api/petApi';
import { AvatarLayout } from '../../../avatar/avatar-layout';
import { AvatarButtonDisplayFormat } from '../../../avatar/AvatarEnums';
import { usePetForm } from '../../PetFormContext/PetFormContext';
import isFormattedImage from '../../../../util/isFormattedImage';
import getImageUrl from '../../../../util/getImageUrl';
import { ApiPetProfileAvatarImageSourceEnum } from '../../../../api/generated-client/PetProfile-Service-A.v1/models';
import { scrollBody } from '../../../../util/scrollBody';

const AvatarSelectionEdit = () => {
  const { formValues, setFormValues, avatarOptions } = usePetForm();
  const imageMutation = useMutation(uploadImage, {
    onSuccess: (data) => {
      setFormValues({
        ...formValues,
        ...{
          avatarId: '0',
          avatarUrl: data,
          avatarSource: ApiPetProfileAvatarImageSourceEnum.Photo,
        },
      });
      setIsImageLoading(true);
    },
  });

  const avatarUrl = formValues.avatarUrl;
  const avatarId = formValues.avatarId;
  const [isImageLoading, setIsImageLoading] = useState(false);

  const [showDrawer, setShowDrawer] = useState(false);
  const [userSelectedAvatarUrl, setUserSelectedAvatarUrl] = useState(`${avatarUrl}`);
  const [userSelectedAvatarId, setUserSelectedAvatarId] = useState(avatarId);

  const getImage = (image: string) =>
    isFormattedImage(image) ? image : getImageUrl(image, 125, 'SS');

  const onUploadCustomAvatar = (selectedImage: Blob) => {
    imageMutation.mutate({ body: selectedImage });
    setShowDrawer(false);
    scrollBody(true);
  };

  const setNewAvatar = (newAvatarUrl: string, newAvatarId: string) => {
    setUserSelectedAvatarUrl(newAvatarUrl);
    setUserSelectedAvatarId(`${newAvatarId}`);
  };

  const saveNewAvatar = () => {
    setFormValues({
      ...formValues,
      ...{
        avatarId: userSelectedAvatarId,
        avatarUrl: userSelectedAvatarUrl,
        avatarSource: ApiPetProfileAvatarImageSourceEnum.Content,
      },
    });
  };

  return (
    <>
      <div className="pet-avatar-section">
        {isImageLoading && !imageMutation.isLoading ? (
          <KibSpinner
            className={classNames('avatar-selection-sheet__loading')}
            data-testid="avatar-selection-image-loader"
          />
        ) : null}
        {imageMutation.isLoading ? (
          <KibSpinner
            className={classNames('avatar-selection-sheet__loading')}
            data-testid="avatar-selection-mutation-loader"
          />
        ) : (
          <img
            alt="pet avatar"
            src={getImage(formValues.avatarUrl)}
            data-testid="edit-form-avatar-image"
            className={classNames('pet-avatar', 'avatar-selection-sheet__avatar-img', {
              loading: isImageLoading,
            })}
            onLoad={() => {
              setIsImageLoading(false);
            }}
          />
        )}
        <div>
          <h1 className="pet-avatar-section__title">{formValues.name}&apos;s Profile</h1>
          <button
            aria-label="Select a new avatar for your pet"
            className="pet-avatar-section__button"
            onClick={() => {
              setShowDrawer(true);
              scrollBody(false);
            }}
            type="button"
            data-testid="edit-form-avatar-image-button"
            disabled={(isImageLoading && !imageMutation.isLoading) || imageMutation.isLoading}
          >
            Change Photo
          </button>
        </div>
      </div>

      {showDrawer && (
        <KibSheet
          open={showDrawer}
          onClose={() => {
            setShowDrawer(false);
            scrollBody(true);
          }}
          className="avatar-selection-sheet"
          title="Change Photo"
          size="large"
          data-testid="avatar-selection-sheet"
        >
          <div className="avatar-selection-sheet__content">
            {avatarOptions?.avatars ? (
              <>
                <PetAvatarUploadButton click={onUploadCustomAvatar} />
                <KibDivider className="avatar-selection-sheet__divider" />
                <AvatarLayout
                  onSetNewAvatar={setNewAvatar}
                  selected={userSelectedAvatarUrl}
                  items={avatarOptions?.avatars || []}
                  type={AvatarButtonDisplayFormat.CIRCLE}
                />
              </>
            ) : (
              <KibSpinner />
            )}

            <div className="avatar-selection-sheet__button-container">
              <KibButtonNew
                type="button"
                className="avatar-selection-sheet__confirm-button"
                onClick={() => {
                  saveNewAvatar();
                  setShowDrawer(false);
                  scrollBody(true);
                }}
                data-testid="confirm-button-avatar-selection-drawer"
              >
                Confirm
              </KibButtonNew>
            </div>
          </div>
        </KibSheet>
      )}
    </>
  );
};

export default AvatarSelectionEdit;
